import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Confirm, EmptyState, FileField, useMeta, TxFilters } from "../components/Shared";
import ReceiptModal from "../components/ReceiptModal";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CreditCard, Printer, Trash2, CheckCircle2, XCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";

export default function Payments() {
  const { isRole } = useAuth();
  const meta = useMeta();
  const [form, setForm] = useState({ purpose: "Salary", purpose_detail: "", employee_id: "", project_id: "", amount: "", payment_method: "Cash", reference: "", remarks: "" });
  const [fileId, setFileId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [nextNums, setNextNums] = useState({ reference: "", check_number: "" });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date_from: "", date_to: "", project_id: "all" });
  const canManage = isRole(ROLES.FM);
  const canDelete = isRole(ROLES.FM);
  const canApprove = isRole(ROLES.FM);

  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const payParams = {};
  if (filters.date_from) payParams.date_from = filters.date_from;
  if (filters.date_to) payParams.date_to = filters.date_to;
  if (filters.project_id !== "all") payParams.project_id = filters.project_id;
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["payments", filters], queryFn: () => api.get("/payments", { params: payParams }).then((r) => r.data) });
  const load = () => qc.invalidateQueries();
  const loadNextNums = useCallback(() => { if (!isRole(ROLES.FM)) return; api.get("/payments/next-numbers").then(({ data }) => setNextNums(data)).catch(() => {}); }, [isRole]);
  useEffect(loadNextNums, [loadNextNums]);

  const q = search.trim().toLowerCase();
  const shown = q ? rows.filter((r) => [r.reference, r.check_number, r.purpose, r.purpose_detail, r.employee_name, r.project_name, r.remarks, r.status, String(r.amount)].filter(Boolean).join(" ").toLowerCase().includes(q)) : rows;

  const submit = async () => {
    if (!form.amount) return;
    if (!form.project_id) { toast.error("Please select a project — payments must be linked to a project."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments", {
        ...form, amount: Number(form.amount),
        employee_id: form.employee_id || null, project_id: form.project_id || null, file_id: fileId,
      });
      toast.success("Payment recorded");
      setReceipt(data);
      setForm({ ...form, amount: "", reference: "", remarks: "", purpose_detail: "" });
      setFileId(null);
      load();
      loadNextNums();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (r) => {
    try { await api.delete(`/payments/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const approve = async (r) => {
    try { await api.patch(`/payments/${r.id}/approve`); toast.success("Payment approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (r) => {
    try { await api.patch(`/payments/${r.id}/reject`); toast.success("Payment rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Payments" subtitle="Record employee and project payments, print vouchers">
        <ExportMenu dataset="payments" filename="stems_payments" testId="payments-export" />
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2"><CreditCard className="h-4 w-4 text-blue-600" /> Make a New Payment</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="payment-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.payment_purposes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.purpose === "Other" && (
              <div className="space-y-1.5">
                <Label className="text-sm">Specify Purpose</Label>
                <Input data-testid="payment-purpose-detail" value={form.purpose_detail} onChange={(e) => setForm({ ...form, purpose_detail: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="payment-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Project <span className="text-red-500">*</span></Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="payment-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm">Amount (FCFA)</Label><Input data-testid="payment-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label className="text-sm">Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Cash", "Bank Transfer", "Cheque", "Mobile Money"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm">Payment Reference</Label><Input data-testid="payment-reference" value={nextNums.reference} readOnly tabIndex={-1} className="bg-slate-50 font-mono-num text-slate-600" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Cheque No.</Label><Input data-testid="payment-cheque-no" value={nextNums.check_number} readOnly tabIndex={-1} className="bg-slate-50 font-mono-num text-slate-600" /></div>
            </div>
            <p className="text-xs text-slate-400 -mt-2">Reference &amp; cheque numbers are generated automatically.</p>
            <div className="space-y-1.5"><Label className="text-sm">Remarks</Label><Textarea data-testid="payment-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            <FileField onUploaded={(id) => setFileId(id)} label="Attach supporting document" testId="payment-file" />
            <Button data-testid="make-payment-button" onClick={submit} disabled={saving || !form.amount} className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8]">Make Payment</Button>
          </div>
        </div>
        )}

        <div className={`${canManage ? "lg:col-span-2" : "lg:col-span-3"} bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden`}>
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <h3 className="font-heading font-semibold text-slate-800">Payment Records</h3>
            <div className="relative w-full max-w-xs">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="payment-search" placeholder="Search payments…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
          </div>
          <div className="p-4 pb-0"><TxFilters projects={projects} filters={filters} setFilters={setFilters} testPrefix="payment-filter" /></div>
          {rowsLoading ? <TableSkeleton cols={7} /> : shown.length === 0 ? <EmptyState text={q ? "No payments match your search." : "No payments recorded yet."} /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Reference</TableHead><TableHead>Purpose</TableHead><TableHead>Employee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {shown.map((r) => (
                  <TableRow key={r.id} data-testid={`payment-row-${r.id}`}>
                    <TableCell className="text-slate-600">{fmtDate(r.date)}</TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-600">
                      <div>{r.reference || "—"}</div>
                      {r.check_number && <div className="text-slate-400">{r.check_number}</div>}
                    </TableCell>
                    <TableCell className="font-medium text-slate-800">{r.purpose}{r.purpose_detail ? ` · ${r.purpose_detail}` : ""}</TableCell>
                    <TableCell className="text-slate-600">{r.employee_name || "—"}</TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(r.status === "completed" ? "completed" : r.status === "pending" ? "pending" : "cancelled")}`}>{r.status || "completed"}</span></TableCell>
                    <TableCell className="text-right font-mono-num font-medium">{fmtFCFA(r.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="stems-actions">
                        {canApprove && r.status === "pending" && (
                          <>
                            <Button size="icon" variant="ghost" className="text-emerald-600" data-testid={`approve-payment-${r.id}`} onClick={() => approve(r)} title="Approve"><CheckCircle2 className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="text-red-600" data-testid={`reject-payment-${r.id}`} onClick={() => reject(r)} title="Reject"><XCircle className="h-4 w-4" /></Button>
                          </>
                        )}
                        <Button size="icon" variant="ghost" data-testid={`print-payment-${r.id}`} onClick={() => setReceipt(r)}><Printer className="h-4 w-4" /></Button>
                        {canDelete && <Confirm onConfirm={() => remove(r)} title="Delete payment?" description="This removes the payment and recalculates balances."
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-payment-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      <ReceiptModal open={!!receipt} onClose={() => setReceipt(null)} type="payment" data={receipt} />
    </div>
  );
}
